import { GetCategoriesIcon, GetSportIcon } from 'assets/icons';
import { Checkmark } from 'assets/icons/Checkmark';
import cn from 'classnames';
import { getEventTypeName } from 'features/coupon/utility';
import { PromoConditionsRowProps } from 'features/PromoConditions';
import { PromoConditionsIcon } from 'features/PromoConditions/components';
import { AnimatePresence, motion } from 'framer-motion';
import React, { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export enum PromoConditionsRowTypes {
	SPORT_ID = 'sportId',
	EVENT_TYPE = 'eventType',
	GAME_TYPE = 'gameType',
	EVENT_ID = 'eventId',
	CATEGORY_ID = 'categoryId',
}

const PromoConditionsRowComponent = ({
	title,
	isComplete,
	iconType,
	value,
	category = [],
	type,
	eventsList,
	categoryMap,
}: PromoConditionsRowProps) => {
	const { t } = useTranslation();

	const hasCategoryItems = category.length;

	const hasValue = !!value;
	if (!hasCategoryItems && !hasValue) return null;

	const renderTextWithComma = (index: number, arrayLength: number, text: string): string => {
		return index < arrayLength - 1 ? `${text}, ` : text;
	};

	const renderCategoryBreadcrumb = (categoryData: any) => {
		const { sportId, parentCategory, parentName, sportName, categoryName, level } = categoryData;
		const isTopLevel = !parentCategory;

		return (
			<div className="flex items-center gap-1">
				<GetSportIcon className="shrink-0 w-3 h-3" id={sportId} />
				{!isTopLevel && <GetCategoriesIcon className="w-3 h-3 shrink-0" category={categoryData} />}

				<div className="flex items-center flex-wrap gap-0.5 text-sm">
					{[sportName, level === 3 ? parentName : null, level >= 2 ? categoryName : null].filter(Boolean).map((item, index) => (
						<React.Fragment key={`promotionCategoryBreadcrumb--${item.sportId}--${index}`}>
							{index > 0 && <span className="text-gray-400">/</span>}
							<span>{item}</span>
						</React.Fragment>
					))}
				</div>
			</div>
		);
	};

	const renderCategoryItems = () => {
		if (!category.length) return null;
		switch (type) {
			case 'categoryIds':
				return category.map((id: number) => {
					const categoryData = categoryMap?.get(id);
					if (!categoryData) return null;
					return <div key={id}>{renderCategoryBreadcrumb(categoryData)}</div>;
				});
			case 'eventTypes':
				return category.map((typeId, index) => (
					<Fragment key={`eventTypesItem-${index}`}>
						{renderTextWithComma(index, category.length, getEventTypeName(t, typeId.toString()))}
					</Fragment>
				));

			case 'gameTypes' || 'eventTypes':
				return (
					<div className="">
						{category.map((itemId, index) => (
							<Fragment key={index}>{renderTextWithComma(index, category.length, t(`marketList_gameType_${itemId}`))}</Fragment>
						))}
					</div>
				);

			case 'eventIds':
				return eventsList?.map((item: any, key: number) => (
					<Link className="text-gray-700 dark:text-gray-300" key={key} to={item.eventUrl}>
						{item.eventName}
					</Link>
				));

			default:
				return category.map((itemId) => (
					<div key={itemId}>
						<span>{itemId}</span>
					</div>
				));
		}
	};

	return (
		<li
			className={cn(
				'grid grid-cols-[auto,1fr] text-paragraph-xs',
				isComplete ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100',
			)}
		>
			<div className="w-5 h-5 flex items-center justify-center">
				<AnimatePresence exitBeforeEnter>
					<motion.div
						key={isComplete ? 'check' : 'icon'}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="w-5 h-5 flex items-center justify-center"
					>
						{isComplete ? <Checkmark className="stroke-gray-700 w-3 h-3" /> : <PromoConditionsIcon name={iconType} />}
					</motion.div>
				</AnimatePresence>
			</div>

			<div className="flex flex-col gap-0.5">
				<div className="flex flex-wrap gap-x-1 leading-5">
					<span className="font-semibold">{t(title)}:</span>
					{hasValue && <span>{value}</span>}
					{type !== 'categoryIds' && renderCategoryItems()}
				</div>
				{type === 'categoryIds' && category.length > 0 && <div className="flex flex-col gap-0.5">{renderCategoryItems()}</div>}
			</div>
		</li>
	);
};

export const PromoConditionsRow = memo(PromoConditionsRowComponent);
